import React, { useEffect, useMemo, useRef, useState, useContext } from 'react';
import { Box, Grid, Text, ResponsiveContext, Avatar, Button, Collapsible } from 'grommet';
import { getWeekScoreboard } from '../espnReader';
import {
  totalScore, projectedPoints, getTeamStats, proGamesByTeam, isEliminatedTeam, isZombieTeam, getDangerZone,
} from '../util';
import { getSeasonConfig, eliminationsForWeek } from '../seasons';
import TeamName from './TeamName';

const NO_GAMES = [];

const LeagueScoreboardByScore = ({ leagueId, season, refreshKey, proGames = NO_GAMES }) => {
  const [rawTeams, setRawTeams] = useState([]); // Raw teams data fetched from the API
  const [teamsList, setTeamsList] = useState([]); // Sorted teams list
  const [sortType, setSortType] = useState('total'); // State for sorting by total or projected
  const [showEliminated, setShowEliminated] = useState(false); // State for showing eliminated teams
  const [status, setStatus] = useState('loading'); // 'loading' | 'ready' | 'error'
  const [week, setWeek] = useState(null); // { matchupPeriodId, scoringPeriodId }
  const hasLoaded = useRef(false);
  const size = useContext(ResponsiveContext); // Get screen size

  useEffect(() => {
    let cancelled = false;
    // Fetch the current week's matchups with team info
    // A live refresh updates in place: no loading flash, and a failed
    // refresh keeps the last good scores on screen.
    const isRefresh = hasLoaded.current;
    const fetchMatchups = async () => {
      if (!isRefresh) setStatus('loading');
      try {
        const games = await getWeekScoreboard(leagueId, season);
        if (cancelled) return;
        if (!Array.isArray(games)) {
          if (!isRefresh) setStatus('error');
          return;
        }

        // Collect all teams from both home and away sides. A side can be
        // null on bye weeks / once teams are eliminated, so skip those.
        let allTeams = [];
        games.forEach(game => {
          ['home', 'away'].forEach(side => {
            const team = game[side];
            if (!team) return;
            allTeams.push({
              teamId: team.teamId,
              teamName: team.teamName,
              teamLogo: team.teamLogo,
              teamRecord: team.teamRecord,
              totalPoints: Math.round(totalScore(team) * 100) / 100,
              projectedPoints: Math.round(projectedPoints(team) * 100) / 100,
              isEliminated: isEliminatedTeam(team.teamName),
              isZombie: isZombieTeam(team.teamName),
              // Kept so player game states can be worked out at render time,
              // as the NFL schedule loads and games kick off.
              side: team,
            });
          });
        });
        setRawTeams(allTeams);
        setWeek(games[0] && {
          matchupPeriodId: games[0].matchupPeriodId,
          scoringPeriodId: games[0].scoringPeriodId,
        });
        hasLoaded.current = true;
        setStatus('ready');
      } catch (error) {
        console.error('Error fetching matchups:', error);
        if (!cancelled && !isRefresh) setStatus('error');
      }
    };
    fetchMatchups();
    return () => { cancelled = true; };
  }, [leagueId, season, refreshKey]);

  useEffect(() => {
    // Sort teams whenever rawTeams or sortType changes
    const sortedTeams = [...rawTeams].sort((a, b) => {
      if (sortType === 'total') {
        return b.totalPoints - a.totalPoints;
      } else if (sortType === 'projected') {
        return b.projectedPoints - a.projectedPoints;
      }
      return 0;
    });
    setTeamsList(sortedTeams);
  }, [rawTeams, sortType]);

  // Toggle sorting type
  const toggleSortType = () => {
    setSortType(sortType === 'total' ? 'projected' : 'total');
  };

  // Toggle visibility of eliminated teams
  const toggleEliminatedTeams = () => {
    setShowEliminated(!showEliminated);
  };

  // Determine if the layout should be 1-column or 2-column based on screen size
  const isSmallScreen = size === 'small' || size === 'xsmall';

  // Split teams into active and eliminated teams
  const activeTeams = teamsList.filter(team => !team.isEliminated);
  const eliminatedTeams = teamsList.filter(team => team.isEliminated);

  const gamesByTeam = useMemo(
    () => proGamesByTeam(proGames, week?.scoringPeriodId),
    [proGames, week]
  );
  const now = Date.now();
  const playerCounts = team => {
    const stats = getTeamStats(team.side, gamesByTeam, now);
    return stats && `Players Done: ${stats.playersDone} / Playing: ${stats.playersPlaying} / Remaining: ${stats.playersRemaining}`;
  };

  // The teams in line for elimination this week, by whichever score is sorted on.
  const scoreKey = sortType === 'total' ? 'totalPoints' : 'projectedPoints';
  const eliminations = week ? eliminationsForWeek(getSeasonConfig(season), week.matchupPeriodId) : 0;
  const dangerZone = getDangerZone(teamsList, eliminations, scoreKey);
  // The divider sits above the highest-scoring team in the zone.
  const dangerLineTeamId = dangerZone[dangerZone.length - 1]?.teamId;

  // Calculate the median score
  const medianIndex = Math.floor(activeTeams.length / 2);
  const medianScore = activeTeams[medianIndex-1]?.[sortType === 'total' ? 'totalPoints' : 'projectedPoints'] || 0;

  // Card colors: zombie tint, else blue above the median and red below it.
  const cardColors = team => {
    if (team.isZombie) return { accent: 'zombie', background: 'zombie-light' };
    return team[scoreKey] >= medianScore
      ? { accent: 'brand', background: 'light-2' }
      : { accent: 'status-critical', background: 'light-3' };
  };

  if (status === 'loading') return <Text>Loading teams…</Text>;
  if (status === 'error') return <Text color="status-critical">Couldn't load teams.</Text>;

  return (
      <Box pad="small" flex={false}>
        {/* Button to toggle sorting */}
        <Box direction="row" justify="end" align="center" flex={false}>
          <Button
            label={`Sort by ${sortType === 'total' ? 'Projected Points' : 'Total Points'}`}
            onClick={toggleSortType}
            size="small"
            margin={{ bottom: 'medium' }}
            alignSelf="end"
          />
        </Box>

        {activeTeams.length > 0 ? (
          <Grid
            rows="auto"
            columns={isSmallScreen ? '100%' : ['1/2', '1/2']}
            gap="small"
            responsive
          >
            {activeTeams.map((team, index) => (
              <React.Fragment key={team.teamId}>
                {/* Add Median Score Divider */}
                {index === medianIndex && (
                  <Box align="center" pad={{ vertical: 'small' }} style={{ gridColumn: '1 / -1' }}>
                    <Text weight="bold" color="brand">
                      Median Score: {medianScore}
                    </Text>
                  </Box>
                )}
                {/* Danger Zone Divider */}
                {team.teamId === dangerLineTeamId && (
                  <Box align="center" pad={{ vertical: 'small' }} style={{ gridColumn: '1 / -1' }}>
                    <Text weight="bold" color="status-critical">
                      ⚠ Danger Zone: {eliminations === 1 ? 'lowest score is' : `bottom ${eliminations} are`} eliminated
                    </Text>
                  </Box>
                )}
                <Box
                  border={{ color: cardColors(team).accent, size: 'small' }}
                  round="small"
                  pad="small"
                  background={cardColors(team).background}
                >
                  {/* Team Info */}
                  <Box direction="row" align="center" gap="small">
                    <Avatar src={team.teamLogo} alt={team.teamName} size='small' />
                    <Box>
                        <TeamName name={team.teamName} record={team.teamRecord} />
                        {team.isZombie && (
                          <Text size="small" weight="bold" color="zombie">
                            🧟 Zombie
                          </Text>
                        )}
                        <Text
                          size="large"
                          weight="bold"
                          color={cardColors(team).accent}
                        >
                          {team.totalPoints} (proj: {team.projectedPoints})
                        </Text>
                        {playerCounts(team) && <Text size="small">{playerCounts(team)}</Text>}
                    </Box>
                  </Box>
                </Box>
              </React.Fragment>
            ))}
          </Grid>
        ) : (
          <Text>No active teams.</Text>
        )}

        {/* Collapsible component for eliminated teams */}
        <Box margin={{ top: 'medium' }}>
          <Button
            label={`${showEliminated ? 'Hide' : 'Show'} Eliminated Teams`}
            onClick={toggleEliminatedTeams}
            size="small"
          />
          <Collapsible open={showEliminated}>
            <Box pad="small" margin={{ top: 'small' }}>
              {eliminatedTeams.length > 0 ? (
                <Grid
                  rows="auto"
                  columns={isSmallScreen ? '100%' : ['1/2', '1/2']}
                  gap="small"
                  responsive
                >
                  {eliminatedTeams.map((team) => (
                    <Box
                      key={team.teamId}
                      border={{ color: 'status-disabled', size: 'small' }}
                      round="small"
                      pad="small"
                      background="light-3"
                    >
                      {/* Team Info */}
                      <Box direction="row" align="center" gap="small">
                        <Avatar src={team.teamLogo} alt={team.teamName} size='small' />
                        <Box>
                            <TeamName name={team.teamName} record={team.teamRecord} />
                            <Text size="large" weight="bold" color="status-disabled">
                              {team.totalPoints} (proj: {team.projectedPoints})
                            </Text>
                            {playerCounts(team) && (
                              <Text size="small" color="status-disabled">{playerCounts(team)}</Text>
                            )}
                        </Box>
                      </Box>
                    </Box>
                  ))}
                </Grid>
              ) : (
                <Text>No eliminated teams</Text>
              )}
            </Box>
          </Collapsible>
        </Box>
      </Box>
  );
};

export default LeagueScoreboardByScore;

